import { useEffect, useState } from 'react';
import { getStorefront } from './api/client';
import { probeDefinitions, runProbe } from './state/probes';
import type { ProbeOutcome } from './state/probes';

const left = { textAlign: 'left' } as const;

/**
 * The Access view: the probe matrix. One row per access probe, run individually so denials
 * render as rows, not failures — a 401/403 here is the ADR-017 enforcement working. Grants are
 * managed in the Tilgong Manager; this page only observes their effect.
 */
export function AccessPanel() {
  const [tenantIds, setTenantIds] = useState<readonly string[]>(['test-fake']);
  const [tenant, setTenant] = useState('test-fake');
  const [results, setResults] = useState<Readonly<Record<string, ProbeOutcome>> | null>(null);
  const [running, setRunning] = useState(false);

  // The tenant options are the storefront's seeded test tenants; on a fetch failure the
  // selector just keeps its default — the probes themselves are unaffected.
  useEffect(() => {
    getStorefront()
      .then((s) => setTenantIds(s.tenants.map((t) => t.tenantId)))
      .catch(() => undefined);
  }, []);

  async function run(): Promise<void> {
    setRunning(true);
    const outcomes = await Promise.all(
      probeDefinitions(tenant).map(async (d) => [d.id, await runProbe(d)] as const),
    );
    setResults(Object.fromEntries(outcomes));
    setRunning(false);
  }

  return (
    <section>
      <h2>Access probes</h2>
      <p>
        Each row asks the backend for a resource with a different access requirement. Grants are
        managed in the Tilgong Manager (dev.tilgong.com) — a <strong>401</strong> or{' '}
        <strong>403</strong> row is the enforcement working, not the storefront failing.
      </p>

      <p>
        <label>
          Tenant for the member/owner probes:{' '}
          <select value={tenant} onChange={(e) => setTenant(e.currentTarget.value)}>
            {tenantIds.map((id) => (
              <option key={id} value={id}>
                {id}
              </option>
            ))}
          </select>
        </label>{' '}
        <button onClick={() => void run()} disabled={running}>
          Run probes
        </button>
      </p>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={left}>Probe</th>
            <th style={left}>Requires</th>
            <th style={left}>Status</th>
            <th style={left}>Code</th>
            <th style={left}>Outcome</th>
          </tr>
        </thead>
        <tbody>
          {probeDefinitions(tenant).map((definition) => {
            const outcome = results?.[definition.id];
            return (
              <tr key={definition.id}>
                <td>{definition.label}</td>
                <td>{definition.requires}</td>
                {outcome === undefined ? (
                  <td colSpan={3}>
                    <em>not run</em>
                  </td>
                ) : (
                  <>
                    <td>{outcome.status}</td>
                    <td>{outcome.code === null ? '—' : <code>{outcome.code}</code>}</td>
                    <td style={{ color: outcome.allowed ? '#0a7d33' : '#b00020' }}>
                      {outcome.allowed ? 'allowed' : 'denied'} — {outcome.detail}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}
